import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { PopoverBody } from 'reactstrap';
import { LabelIcon } from 'shared/components/Labels';

import useApi from 'hooks/useApi';
import {
  loadAllNotificationsMassive,
  searchNotificationsMassive,
  setClearNotificationsMassiveList,
  changeSortConfigNotificationMassive,
  setMassiveAll,
  setCurrentNotification,
} from 'redux/actions/notificationActions';

import { colors } from 'shared/defaultTheme';
import { ButtonLabelIcon } from 'shared/components/Buttons';

import { ReactComponent as CheckRoundIcon } from 'assets/icon-check-round.svg';
import { ReactComponent as SortByIcon } from 'assets/icon-sort-by.svg';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';

import { Popover, OptionMenu, SerachNotification } from '../styles';
import TableMassive from './NotificationsMassiveTable';

const NotificationMassive = ({ setOpenFormModal }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { customAreaApi } = useApi();
  const { cusNavId, isCustomer, mail, clientId } = useSelector((state) => state.auth);
  const notificationState = useSelector((state) => state.notification);
  const { searchMassiveValue, sortConfigMassive } = notificationState;
  const [orderName, setOrderName] = useState('Created');
  const [isOrder, setIsOrder] = useState(false);

  const [{ loading }, getNotificationsMassiveData] = customAreaApi(
    {
      url: `${window.API_V1}/gen/not/${cusNavId}/search?login=${mail}`,
      method: 'POST',
    },
    { manual: true }
  );

  const onChangeSearch = (e) => {
    if (e) {
      const { value } = e.target;
      dispatch(searchNotificationsMassive(value));
    } else {
      dispatch(searchNotificationsMassive(''));
    }
  };

  const onSort = (fieldName, isSort) => {
    dispatch(changeSortConfigNotificationMassive({ fieldName, isSort }));
  };

  useEffect(() => {
    onSort(orderName, isOrder);
  }, [orderName, isOrder]);

  const notificationsMassiveData = async (searchParam = undefined) => {
    try {
      const {
        data: { errors, data },
      } = await getNotificationsMassiveData(searchParam ? { data: searchParam } : undefined);
      if (errors.length) {
        return;
      }
      const today = new Date();
      const filterNotification = isCustomer ? data : data.filter((x) => today >= new Date(x.date));
      dispatch(loadAllNotificationsMassive(filterNotification));
      dispatch(setMassiveAll(filterNotification));
    } catch (ex) {
      dispatch(loadAllNotificationsMassive([]));
      dispatch(setMassiveAll([]));
    }
  };

  const searchNotificationMassive = () => {
    const sortOption = sortConfigMassive.find(
      (currentSortOption) => currentSortOption.asc !== 'none'
    );
    const filters = sortOption
      ? [
          ...sortConfigMassive,
          {
            model: 'GeneralNotify',
            field: 'Name',
            filter:
              searchMassiveValue && searchMassiveValue.length >= 2 ? [searchMassiveValue] : null,
            isRegex: !!(searchMassiveValue && searchMassiveValue.length >= 2),
          },
        ]
      : [
          {
            model: 'GeneralNotify',
            field: 'Id',
            asc: false,
          },
          {
            model: 'GeneralNotify',
            field: 'Name',
            filter:
              searchMassiveValue && searchMassiveValue.length >= 2 ? [searchMassiveValue] : null,
            isRegex: !!(searchMassiveValue && searchMassiveValue.length >= 2),
            asc: true,
          },
        ];
    if (!isCustomer) {
      filters.push({
        model: 'GeneralNotify',
        field: 'LinkToContacts.Mail',
        filter: [mail],
      });
      filters.push({
        model: 'GeneralNotify',
        field: 'ClientId',
        filter: [clientId],
      });
    }
    const searchParam = {
      filters,
      reduce: {
        limit: 0,
        skip: 0,
      },
    };
    notificationsMassiveData(searchParam);
  };

  useEffect(() => {
    searchNotificationMassive();
  }, [sortConfigMassive]);

  useEffect(() => {
    if (searchMassiveValue != null && !loading) {
      searchNotificationMassive();
    }
  }, [searchMassiveValue]);

  const getSortIcon = (fieldName) => {
    const sortOption = sortConfigMassive.find(
      (currentSortOption) => currentSortOption.field === fieldName
    );
    if (sortOption) {
      return <CheckRoundIcon fill={colors.black} />;
    }
    return <CheckRoundIcon fill={colors.white} />;
  };

  const getOrderIcon = (isSort) => {
    const sortOption = sortConfigMassive.find(
      (currentSortOption) => currentSortOption.asc === isSort
    );
    if (sortOption) {
      return <CheckRoundIcon fill={colors.black} />;
    }
    return <CheckRoundIcon fill={colors.white} />;
  };

  const handleRefresh = () => {
    onSort('Created', false);
    dispatch(setClearNotificationsMassiveList(true));
  };

  return (
    <>
      <div className="d-flex justify-content-end action-header-mobile">
        <div className="form d-flex justify-content-end w-25 h-50 mr-4 w-container-search-mobile">
          <SerachNotification
            Icon={() => null}
            borderRadius={2}
            className="mb-4 w-search-notification m-0 h-50"
            inputClassName="w-100"
            buttonClassName=""
            rightIcon={<SearchIcon fill={colors.black} />}
            onChange={onChangeSearch}
            value={searchMassiveValue ?? ''}
            placeholder={t('notification_search')}
          />
        </div>
        <div className="d-flex container-action-button">
          <ButtonLabelIcon
            text={t('general_order_by')}
            type="button"
            Icon={() => <SortByIcon fill={colors.black} />}
            className="button-icon mr-2"
            id="PopoverFocusSort"
          />
          <ButtonLabelIcon
            id="refresh-icon"
            text={t('general_button_refresh')}
            type="button"
            Icon={() => <RefreshIcon fill={colors.black} />}
            className="button-icon mr-2"
            onClick={handleRefresh}
          />
          <ButtonLabelIcon
            id="add-icon"
            text={t('general_button_new')}
            type="button"
            Icon={() => <AddIcon fill={colors.black} />}
            className="button-icon mr-2"
            onClick={() => {
              dispatch(setCurrentNotification(null));
              setOpenFormModal(true);
            }}
          />
        </div>
      </div>
      <Popover trigger="legacy" placement="bottom" target="PopoverFocusSort">
        <PopoverBody>
          <OptionMenu onClick={() => setOrderName('Name')}>
            <LabelIcon
              label={t('notification_column_issue')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getSortIcon('Name')}
            />
          </OptionMenu>
          <OptionMenu onClick={() => setOrderName('Created')}>
            <LabelIcon
              label={t('notification_column_created_date')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getSortIcon('Created')}
            />
          </OptionMenu>
          <OptionMenu onClick={() => setOrderName('Date')}>
            <LabelIcon
              label={t('notification_column_start_date')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getSortIcon('Date')}
            />
          </OptionMenu>
          <hr />
          <OptionMenu onClick={() => setIsOrder(true)}>
            <LabelIcon
              label={t('general_ascending')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getOrderIcon(true)}
            />
          </OptionMenu>
          <OptionMenu onClick={() => setIsOrder(false)}>
            <LabelIcon
              label={t('general_descending')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getOrderIcon(false)}
            />
          </OptionMenu>
          <OptionMenu onClick={() => setIsOrder('none')}>
            <LabelIcon
              label={t('general_none')}
              color=""
              className="ml-1 body-text--primary action-option-order-label"
              classNameMain="mb-3 label-icon-sort"
              isStyle={false}
              Icon={() => getOrderIcon('none')}
            />
          </OptionMenu>
        </PopoverBody>
      </Popover>
      <TableMassive
        handleRefresh={handleRefresh}
        notificationsMassiveData={notificationsMassiveData}
        loading={loading}
        setOpenFormModal={setOpenFormModal}
      />
    </>
  );
};

NotificationMassive.propTypes = {
  setOpenFormModal: PropTypes.func.isRequired,
};

export default NotificationMassive;
